import React, { useState } from 'react';
import { AlertTriangleIcon, CheckCircleIcon, ClockIcon, FlagIcon, PlusCircleIcon, XCircleIcon, ArrowLeftRightIcon, ScissorsIcon, ChevronDownIcon, ChevronUpIcon, LayersIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormat';
export function ExceptionQueue({
  exceptions,
  selectedException,
  onSelectException,
  statusFilter,
  typeFilter,
  onStatusFilterChange,
  onTypeFilterChange,
  onMovePage,
  onSplitDocument
}) {
  const [expandedDocumentId, setExpandedDocumentId] = useState(null);
  const [showAllPages, setShowAllPages] = useState(false);
  // Toggle page thumbnails visibility for a document
  const togglePageThumbnails = (documentId, e) => {
    e.stopPropagation();
    setExpandedDocumentId(expandedDocumentId === documentId ? null : documentId);
  };
  // Toggle show all pages
  const toggleShowAllPages = () => {
    setShowAllPages(!showAllPages);
    // Reset individual expanded state when toggling global state
    setExpandedDocumentId(null);
  };
  // Check if pages should be shown for a document
  const shouldShowPagesFor = documentId => {
    return showAllPages || expandedDocumentId === documentId;
  };
  // Get status icon based on exception status
  const getStatusIcon = status => {
    switch (status) {
      case 'New':
        return <AlertTriangleIcon className="h-4 w-4 text-red-500" data-id="element-5589" />;
      case 'In Progress':
        return <ClockIcon className="h-4 w-4 text-blue-500" data-id="element-5590" />;
      case 'Waiting Approval':
        return <FlagIcon className="h-4 w-4 text-yellow-500" data-id="element-5591" />;
      case 'Resolved':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" data-id="element-5592" />;
      case 'Rejected':
        return <XCircleIcon className="h-4 w-4 text-gray-400" data-id="element-5593" />;
      default:
        return <AlertTriangleIcon className="h-4 w-4 text-gray-400" data-id="element-5594" />;
    }
  };
  // Get confidence level class
  const getConfidenceClass = confidence => {
    if (confidence >= 0.8) return 'bg-green-100 text-green-800';
    if (confidence >= 0.6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };
  // Handle page move action
  const handleMovePage = (sourceDocId, pageIndex, e) => {
    e.stopPropagation();
    if (onMovePage) {
      onMovePage(sourceDocId, pageIndex);
    }
  };
  // Handle document split action
  const handleSplitDocument = (documentId, pageIndex, e) => {
    e.stopPropagation();
    if (onSplitDocument) {
      onSplitDocument(documentId, pageIndex);
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-5595">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-5596">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-5597">
          Exception Queue
        </h3>
        <div className="flex items-center space-x-2" data-id="element-5598">
          <button onClick={toggleShowAllPages} className="inline-flex items-center px-2 py-1 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" title={showAllPages ? 'Hide all pages' : 'Show all pages'} data-id="element-5599">
            <LayersIcon className="h-4 w-4 mr-1" data-id="element-5600" />
            {showAllPages ? <ChevronUpIcon className="h-3 w-3" data-id="element-5601" /> : <ChevronDownIcon className="h-3 w-3" data-id="element-5602" />}
          </button>
        </div>
      </div>
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50" data-id="element-5603">
        <div className="flex space-x-2" data-id="element-5604">
          <select value={statusFilter} onChange={e => onStatusFilterChange(e.target.value)} className="block w-full pl-3 pr-10 py-1 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md" data-id="element-5605">
            <option value="All" data-id="element-5606">All Statuses</option>
            <option value="New" data-id="element-5607">New</option>
            <option value="In Progress" data-id="element-5608">In Progress</option>
            <option value="Waiting Approval" data-id="element-5609">Waiting Approval</option>
          </select>
          <select value={typeFilter} onChange={e => onTypeFilterChange(e.target.value)} className="block w-full pl-3 pr-10 py-1 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md" data-id="element-5610">
            <option value="All" data-id="element-5611">All Types</option>
            <option value="PO Invoice" data-id="element-5612">PO Invoice</option>
            <option value="Non-PO Invoice" data-id="element-5613">Non-PO Invoice</option>
            <option value="Credit Note" data-id="element-5614">Credit Note</option>
          </select>
        </div>
      </div>
      <ul className="divide-y divide-gray-200 max-h-[70vh] overflow-y-auto" data-id="element-5615">
        {exceptions.length === 0 ? <li className="px-4 py-6 text-center text-sm text-gray-500" data-id="element-5616">
            No exceptions matching the selected filters
          </li> : exceptions.map(exception => <li key={exception.id} className={`cursor-pointer ${selectedException?.id === exception.id ? 'bg-indigo-50' : 'hover:bg-gray-50'}`} data-id="element-5617">
              <div className="px-4 py-4" onClick={() => onSelectException(exception)} data-id="element-5618">
                <div className="flex items-center justify-between" data-id="element-5619">
                  <div className="flex items-center" data-id="element-5620">
                    {getStatusIcon(exception.status)}
                    <span className="ml-2 text-sm font-medium text-gray-900" data-id="element-5621">
                      {exception.vendor}
                    </span>
                  </div>
                  <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getConfidenceClass(exception.confidence)}`} data-id="element-5622">
                    {Math.round(exception.confidence * 100)}%
                  </div>
                </div>
                <div className="mt-2 flex justify-between" data-id="element-5623">
                  <div className="text-sm text-gray-500" data-id="element-5624">
                    {exception.detectedType || 'Unknown Type'}
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-5625">
                    {formatCurrency(exception.amount, 'AUD')}
                  </div>
                </div>
                <div className="mt-2 flex justify-between items-center" data-id="element-5626">
                  <div className="text-xs text-gray-500" data-id="element-5627">{exception.date}</div>
                  {!showAllPages && <button onClick={e => togglePageThumbnails(exception.id, e)} className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center" data-id="element-5628">
                      {expandedDocumentId === exception.id ? 'Hide Pages' : 'Show Pages'}
                    </button>}
                </div>
              </div>
              {/* Page Thumbnails Section */}
              {shouldShowPagesFor(exception.id) && <div className="px-4 pb-4 pt-1 border-t border-gray-100" data-id="element-5629">
                  <div className="text-xs font-medium text-gray-500 mb-2" data-id="element-5630">
                    Pages
                  </div>
                  <div className="flex space-x-2 overflow-x-auto pb-2" data-id="element-5631">
                    {exception.pages && exception.pages.length > 0 ? exception.pages.map((page, index) => <div key={index} className="relative flex-shrink-0 group" data-id="element-5632">
                          <div className="w-16 h-20 border border-gray-200 rounded overflow-hidden" data-id="element-5633">
                            <img src={page.thumbnail} alt={`Page ${index + 1}`} className="w-full h-full object-cover" data-id="element-5634" />
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs py-1 text-center" data-id="element-5635">
                            {index + 1}
                          </div>
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-5636">
                            <div className="flex space-x-1" data-id="element-5637">
                              <button onClick={e => handleMovePage(exception.id, index, e)} className="p-1 bg-white rounded-full shadow-sm" title="Move page to another document" data-id="element-5638">
                                <ArrowLeftRightIcon className="h-3 w-3 text-indigo-600" data-id="element-5639" />
                              </button>
                              {index > 0 && <button onClick={e => handleSplitDocument(exception.id, index, e)} className="p-1 bg-white rounded-full shadow-sm" title="Split document at this page" data-id="element-5640">
                                  <ScissorsIcon className="h-3 w-3 text-indigo-600" data-id="element-5641" />
                                </button>}
                            </div>
                          </div>
                        </div>) : <div className="text-xs text-gray-400 italic" data-id="element-5642">
                        No page previews available
                      </div>}
                    <div className="w-16 h-20 border border-dashed border-gray-300 rounded flex items-center justify-center cursor-pointer hover:bg-gray-50" data-id="element-5643">
                      <PlusCircleIcon className="h-6 w-6 text-gray-400" data-id="element-5644" />
                    </div>
                  </div>
                </div>}
            </li>)}
      </ul>
    </div>;
}